import { gql } from '@apollo/client'
import { isOwnerRelation } from '~/helpers/resources/boats'

/**
 * Everything the dashboard shows, in one request: the inventory (small — a few
 * dozen boats), the next appointments, the portals' latest figures, and what
 * was changed lately.
 */
export const DASHBOARD = gql`
  query Dashboard($from: timestamptz!, $until: timestamptz!) {
    boats(order_by: [{ name: asc }, { id: asc }]) {
      id
      name
      manufacturer
      model
      status
      asking_price
      offer_price
      lower_price_limit
      photos: attachments_aggregate(where: { content_type: { _like: "image/%" } }) {
        aggregate {
          count
        }
      }
      boat_customer {
        relation_type
      }
    }
    appointments(
      where: { start: { _gte: $from, _lt: $until } }
      order_by: { start: asc }
      limit: 12
    ) {
      id
      title
      type
      start
      allDay
      location
      appointment_boat {
        boat {
          id
          name
          model
        }
      }
      appointment_customer {
        customer {
          id
          firstname
          lastname
        }
      }
      appointment_user {
        user_id
      }
    }
    listings: boatvertizer_listings {
      listing_id
      title
      boat {
        id
        name
        model
      }
      windows(order_by: { window_end: desc }, limit: 1) {
        window_start
        window_end
        detailviews
        inquiries
      }
    }
    account: boatvertizer_account_by_pk(id: 1) {
      last_fetch_at
      last_fetch_ok
    }
    recent: history_transactions(
      where: { relation: { _in: ["boats", "customers"] } }
      order_by: { action_tstamp_tx: desc }
      limit: 60
    ) {
      relation
      relation_id
      operation
      action_tstamp_tx
      user_id
    }
    users {
      id
      displayName
      email
    }
  }
`

/** The customers among the recent changes, by id (names only). */
export const RECENT_CUSTOMERS = gql`
  query DashboardCustomers($ids: [Int!]!) {
    customers(where: { id: { _in: $ids } }) {
      id
      firstname
      lastname
      company
    }
  }
`

export interface DashBoat {
  id: number
  name: string | null
  manufacturer?: string | null
  model: string | null
  status: string | null
  asking_price: number | null
  offer_price: number | null
  lower_price_limit: number | null
  photos: { aggregate: { count: number } }
  boat_customer: { relation_type: string | null }[]
}

export interface DashAppointment {
  id: number
  title: string | null
  type: string | null
  start: string
  allDay: boolean | null
  location: string | null
  appointment_boat: { boat: { id: number; name: string | null; model: string | null } | null }[]
  appointment_customer: {
    customer: { id: number; firstname: string | null; lastname: string | null } | null
  }[]
  appointment_user: { user_id: string }[]
}

export interface DashListing {
  listing_id: string
  title: string | null
  boat: { id: number; name: string | null; model: string | null } | null
  windows: {
    window_start: string
    window_end: string
    detailviews: number | null
    inquiries: number | null
  }[]
}

export interface DashChange {
  relation: string
  relation_id: number
  operation: string
  action_tstamp_tx: string
  user_id: string
}

export interface DashboardData {
  boats: DashBoat[]
  appointments: DashAppointment[]
  listings: DashListing[]
  account: { last_fetch_at: string | null; last_fetch_ok: boolean | null } | null
  recent: DashChange[]
  users: { id: string; displayName: string | null; email: string | null }[]
}

/** "Diva (Diva 39)": the boat's name, and its model when that says more. */
export const boatLabel = (b: { id: number; name?: string | null; model?: string | null }) => {
  const name = b.name?.trim()
  const model = b.model?.trim()
  if (name && model && name.toLowerCase() !== model.toLowerCase()) return `${name} (${model})`
  return name || model || `#${b.id}`
}

/** Statuses in the order of a sale; boats without one are counted apart. */
export const STATUS_ORDER = [
  'processing',
  'mediation',
  'handover',
  'settlement',
  'completed',
  'cancelled',
]

/** How many boats per status (`null` for those without one). */
export const countByStatus = (boats: DashBoat[]) => {
  const counts = new Map<string | null, number>()
  for (const b of boats) {
    const key = b.status && STATUS_ORDER.includes(b.status) ? b.status : null
    counts.set(key, (counts.get(key) ?? 0) + 1)
  }
  return counts
}

/** The boats being worked on or offered: what the to-do list looks at. */
const ACTIVE = new Set(['processing', 'mediation'])

export type TodoKey = 'no_photos' | 'no_price' | 'no_owner' | 'below_limit'

/**
 * What is missing on the boats being worked on or offered, rule by rule, each
 * with its boats. Only rules that apply to at least one boat are returned.
 */
export function todos(boats: DashBoat[]): { key: TodoKey; boats: DashBoat[] }[] {
  const active = boats.filter((b) => b.status && ACTIVE.has(b.status))
  const rules: { key: TodoKey; test: (b: DashBoat) => boolean }[] = [
    { key: 'no_photos', test: (b) => (b.photos?.aggregate?.count ?? 0) === 0 },
    { key: 'no_price', test: (b) => !(Number(b.asking_price) > 0) },
    {
      key: 'no_owner',
      test: (b) => !b.boat_customer.some((c) => isOwnerRelation(c.relation_type)),
    },
    {
      key: 'below_limit',
      test: (b) =>
        Number(b.offer_price) > 0 &&
        Number(b.lower_price_limit) > 0 &&
        Number(b.offer_price) < Number(b.lower_price_limit),
    },
  ]
  return rules
    .map((r) => ({ key: r.key, boats: active.filter(r.test) }))
    .filter((r) => r.boats.length > 0)
}

/**
 * The portals' latest figures: the listings' windows from the latest fetch,
 * summed, and the listings that drew the most inquiries (then views). A
 * listing Boatvertizer no longer reports keeps an old window; it is left out.
 */
export function portalFigures(listings: DashListing[], top = 5) {
  const all = listings.map((l) => ({ listing: l, window: l.windows[0] })).filter((r) => r.window)
  const latest = all.map((r) => r.window.window_end).sort()[all.length - 1]
  const rows = all.filter((r) => r.window.window_end === latest)
  const sum = (key: 'detailviews' | 'inquiries') =>
    rows.reduce((total, r) => total + (r.window[key] ?? 0), 0)
  const starts = rows.map((r) => r.window.window_start).sort()
  const ends = rows.map((r) => r.window.window_end).sort()
  return {
    views: sum('detailviews'),
    inquiries: sum('inquiries'),
    from: starts[0] ?? null,
    to: ends[ends.length - 1] ?? null,
    top: [...rows]
      .sort(
        (a, b) =>
          (b.window.inquiries ?? 0) - (a.window.inquiries ?? 0) ||
          (b.window.detailviews ?? 0) - (a.window.detailviews ?? 0)
      )
      .slice(0, top),
  }
}

/** The boats and customers changed last, each once, newest first; deleted ones left out. */
export function recentlyChanged(changes: DashChange[], boatIds: Set<number>, limit = 8) {
  const seen = new Set<string>()
  const out: DashChange[] = []
  const deleted = new Set(
    changes.filter((c) => c.operation === 'DELETE').map((c) => `${c.relation}:${c.relation_id}`)
  )
  for (const c of changes) {
    const key = `${c.relation}:${c.relation_id}`
    if (seen.has(key) || deleted.has(key)) continue
    if (c.relation === 'boats' && !boatIds.has(c.relation_id)) continue
    seen.add(key)
    out.push(c)
    if (out.length >= limit) break
  }
  return out
}
